import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, where, getDocs, onSnapshot, writeBatch, serverTimestamp, doc, getDoc, addDoc, orderBy, Timestamp, collectionGroup, deleteField, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, storage, getClassPointRankingMode, getTotalEarnedPointsByStudent, getClassPoints, resolveTeacherClassId } from '../../../utils/firebase';
import { School, Teacher, User, isTeacher, isSuperAdmin } from '../../../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { 
  subscribeToUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  getUnreadNotificationCount,
  getNotificationTypeColor,
  getNotificationTypeIcon,
  getNotificationBackgroundColor,
  createItemNotification
} from '../../../utils/notifications';
import { Notification } from '../../../types/user';
import { Schedule } from '../../../types/schedule';
import PlusCircleIcon from '../../../components/icons/PlusCircleIcon';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { resolveTeacherPlan } from '../../../config/teacherPlans';
import styles from './ClassTeacherHome.module.css';
import TimetableEditDialog from '../../../components/ui/TimetableEditDialog';
import TodayMealCard from '../../../components/ui/TodayMealCard';
import EditIcon from '../../../components/icons/EditIcon';
import AddNewNoticeDialog, { NoticeItem as AddNoticeItem, NoticeFormValues } from '../../../components/ui/AddNewNoticeDialog';
import AddClassPointsDialog from '../../../components/ui/AddClassPointsDialog';
import { getCurrentTimetableStatus } from '../../../utils/timetablePeriod';

interface RankData {
  id: string;
  name: string;
  points: number;
  stamps: number | { count: number };
  studentNumber?: number;
}

// 타입 가드 함수 추가
const isStampObject = (stamps: number | { count: number }): stamps is { count: number } => {
  return typeof stamps === 'object' && stamps !== null && 'count' in stamps;
};

interface PointHistory {
  id: string;
  studentId: string;
  studentName: string;
  amount: number;
  type: 'add' | 'subtract' | 'use' | 'oneTime';
  reason: string;
  createdAt: Date;
  teacherId: string;
  teacherName: string;
}

// 아이템 사용 승인 요청 인터페이스 추가
interface ItemApprovalRequest {
  id: string;
  studentId: string;
  studentName: string;
  itemId: string;
  itemName: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  updatedAt?: Date;
  reason?: string;
  targetType: 'class' | 'subject';
  studentClassId: string;
  studentGrade: number;
  studentClass: number;
}

interface TeacherUser {
  id: string;
  name: string;
  email: string;
  role: string;
  schoolId: string;
  homeroomClass?: {
    id: string;
    grade: number;
    class: number;
  };
  subject?: 'art' | 'tech';
  classId?: string;
  subjectId?: string;
  uid: string;
}

/** Firestore YYYY-MM-DD → 화면 표시 `2026.03.02-07.20` (같은 해) / 연도 다르면 끝도 전체 표기 */
const formatSemesterPeriodLabel = (startDate: string, endDate: string): string => {
  const parse = (raw: string): { y: number; m: number; d: number } | null => {
    if (!raw || typeof raw !== 'string') return null;
    const s = raw.trim().replace(/\./g, '-');
    const match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    return { y: +match[1], m: +match[2], d: +match[3] };
  };
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const start = parse(startDate);
  const end = parse(endDate);
  if (!start || !end) return `${startDate}-${endDate}`;
  const head = `${start.y}.${pad2(start.m)}.${pad2(start.d)}`;
  if (start.y === end.y) return `${head}-${pad2(end.m)}.${pad2(end.d)}`;
  return `${head}-${end.y}.${pad2(end.m)}.${pad2(end.d)}`;
};

const ClassTeacherHome: React.FC = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<School | null>(null);
  const [pointRankings, setPointRankings] = useState<RankData[]>([]);
  const [stampRankings, setStampRankings] = useState<RankData[]>([]);
  // 학생 상세 정보 다이얼로그 상태
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<RankData | null>(null);
  const [openItemNotFoundDialog, setOpenItemNotFoundDialog] = useState(false);
  const [itemNotFoundRequest, setItemNotFoundRequest] = useState<ItemApprovalRequest | null>(null);
  const [pointHistory, setPointHistory] = useState<PointHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // 아이템 승인 요청 관련 상태 추가
  const [approvalRequests, setApprovalRequests] = useState<ItemApprovalRequest[]>([]);
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ItemApprovalRequest | null>(null);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [studentCount, setStudentCount] = useState<number>(0);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isAddNoticeDialogOpen, setIsAddNoticeDialogOpen] = useState(false);
  const [noticeItems, setNoticeItems] = useState<AddNoticeItem[]>([]);

  // 시간표 다이얼로그 상태 추가
  const [isTimetableDialogOpen, setIsTimetableDialogOpen] = useState(false);
  
  // 학급 사진 관련 상태 (최대 4장)
  const [classPhotos, setClassPhotos] = useState<string[]>(['/images/class/ClassSampleImage.png']);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // 학생 아이템 다이얼로그 상태
  const [openStudentItemsDialog, setOpenStudentItemsDialog] = useState(false);
  const [selectedStudentForItems, setSelectedStudentForItems] = useState<any>(null);
  const [studentItems, setStudentItems] = useState<any[]>([]);
  
  // 알림 관련 상태
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
  
  // 모바일 감지 상태
  const [isMobile, setIsMobile] = useState(false);
  
  // 학기 날짜 상태
  const [semesterDates, setSemesterDates] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);
  // 학급 포인트 (선생님이 학급에 주는 별도 포인트, 개인 포인트 합과 무관)
  const [classPoints, setClassPoints] = useState<number>(0);
  const [isAddClassPointsDialogOpen, setIsAddClassPointsDialogOpen] = useState(false);
  
  // 시간표 데이터 상태
  const [timetableData, setTimetableData] = useState<{
    semester: '1학기' | '2학기';
    year: number;
    entries: {
      period: number;
      time: string;
      monday?: string;
      tuesday?: string;
      wednesday?: string;
      thursday?: string;
      friday?: string;
    }[];
  }>({
    semester: '1학기',
    year: new Date().getFullYear(),
    entries: [
      { period: 1, time: '09:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 2, time: '10:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '영어', friday: '통합 과학' },
      { period: 3, time: '11:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 4, time: '11:30', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 5, time: '13:20', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 6, time: '14:10', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
      { period: 7, time: '15:00', monday: '통합 과학', tuesday: '미술', wednesday: '통합 사회', thursday: '통합 사회', friday: '통합 과학' },
    ]
  });

  // 일정 날짜 비교 헬퍼 함수
  const isScheduleOnDate = (schedule: Schedule, date: number, year?: number, month?: number): boolean => {
    const now = new Date();
    const checkYear = year ?? now.getFullYear();
    const checkMonth = month ?? now.getMonth();
    
    // 날짜만 비교하기 위한 헬퍼 함수 (시간 정보 제거)
    const normalizeDate = (d: Date) => {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };
    
    // allDay 옵션
    if (schedule.dateOption === 'allDay' && schedule.date) {
      const scheduleDate = schedule.date.toDate();
      return scheduleDate.getDate() === date && 
             scheduleDate.getMonth() === checkMonth && 
             scheduleDate.getFullYear() === checkYear;
    }
    
    // custom 옵션
    if (schedule.dateOption === 'custom' && schedule.startDate && schedule.endDate) {
      const startDate = normalizeDate(schedule.startDate.toDate());
      const endDate = normalizeDate(schedule.endDate.toDate());
      const checkDate = new Date(checkYear, checkMonth, date);
      const normalizedCheckDate = normalizeDate(checkDate);
      return normalizedCheckDate >= startDate && normalizedCheckDate <= endDate;
    }
    
    // period 옵션 - 날짜 직접 비교는 어려우므로 false 반환
    return false;
  };

  // 달력 생성 함수
  const generateCalendar = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // 현재 월의 첫 번째 날과 마지막 날
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 첫 번째 날의 요일 (0=일요일, 1=월요일, ...)
    const firstDayOfWeek = firstDay.getDay();
    
    // 현재 월의 일수
    const daysInMonth = lastDay.getDate();
    
    // 이전 달의 마지막 날들 (빈 칸 채우기용)
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    const calendar = [];
    
    // 이전 달의 날짜들 (빈 칸 채우기)
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      calendar.push({
        date: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // 현재 달의 날짜들
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
      calendar.push({
        date: day,
        isCurrentMonth: true,
        isToday
      });
    }
    
    // 다음 달의 날짜들 (빈 칸 채우기)
    const remainingCells = 42 - calendar.length; // 6주 * 7일 = 42칸
    for (let day = 1; day <= remainingCells; day++) {
      calendar.push({
        date: day,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return calendar;
  };

  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !isTeacher(currentUser)) return;

      try {
        setLoading(true);

        // 학교 정보 가져오기
        try {
        const schoolRef = doc(db, 'schools', currentUser.schoolId);
        const schoolDoc = await getDoc(schoolRef);

        if (schoolDoc.exists()) {
          setSchoolInfo(schoolDoc.data() as School);
          }
        } catch (schoolError) {
          console.error('학교 정보 가져오기 실패:', schoolError);
          throw schoolError;
        }

        // 담임 학급이 있는 경우 학생들의 포인트 순위 가져오기
        if (currentUser.homeroomClass) {
          try {
          const studentsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students`);
          const studentsSnapshot = await getDocs(studentsRef);

            // 학생 수 설정
            setStudentCount(studentsSnapshot.size);

            // 학급 사진 불러오기
            try {
              const classRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}`);
              const classDoc = await getDoc(classRef);

              if (classDoc.exists()) {
                const classData = classDoc.data();
                if (classData.photos && Array.isArray(classData.photos) && classData.photos.length > 0) {
                  setClassPhotos(classData.photos);
                }
              }
            } catch (classError) {
              console.error('학급 사진 정보 가져오기 실패:', classError);
            }

            // 시간표 불러오기
            try {
              const timetableRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/timetable/current`);
              const timetableDoc = await getDoc(timetableRef);

              if (timetableDoc.exists()) {
                const timetableData = timetableDoc.data();
                setTimetableData({
                  semester: timetableData.semester || '1학기',
                  year: timetableData.year || new Date().getFullYear(),
                  entries: timetableData.entries || []
                });
              }
            } catch (timetableError) {
              console.error('시간표 정보 가져오기 실패:', timetableError);
            }

            // 학기 날짜 및 학급 포인트 불러오기
            try {
              const classRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}`);
              const classDoc = await getDoc(classRef);

              if (classDoc.exists()) {
                const classData = classDoc.data();
                if (classData?.semesterDates) {
                  setSemesterDates(classData.semesterDates);
                }
                setClassPoints(typeof classData?.classPoints === 'number' ? classData.classPoints : 0);
              }
            } catch (semesterDatesError) {
              console.error('학기 날짜 정보 가져오기 실패:', semesterDatesError);
            }
          
          const schoolId = currentUser.schoolId;
          const classId = currentUser.classId;
          const rankingMode = schoolId && classId
            ? await getClassPointRankingMode(schoolId, classId)
            : 'balance';
          let totalEarnedByStudent: Record<string, number> = {};
          if (rankingMode === 'totalEarned' && schoolId && classId) {
            totalEarnedByStudent = await getTotalEarnedPointsByStudent(schoolId, classId);
          }
          const studentsData = studentsSnapshot.docs.map(doc => {
            const data = doc.data();
            const balance = data.points || 0;
            const rankPoints = rankingMode === 'totalEarned' ? (totalEarnedByStudent[doc.id] || 0) : balance;
            return {
              id: doc.id,
              name: data.name,
              points: rankPoints,
              stamps: data.stamps || 0,
              studentNumber: data.number || null
            };
          }) as RankData[];

          const sortedByPoints = [...studentsData].sort((a, b) => {
            const aPoints = typeof a.points === 'number' ? a.points : 0;
            const bPoints = typeof b.points === 'number' ? b.points : 0;
            return bPoints - aPoints;
          });
          setPointRankings(sortedByPoints);

          // 도장 기준으로 내림차순 정렬
          const sortedByStamps = [...studentsData].sort((a, b) => {
            const aStamps = isStampObject(a.stamps) ? a.stamps.count : a.stamps;
            const bStamps = isStampObject(b.stamps) ? b.stamps.count : b.stamps;
            return bStamps - aStamps;
          });
          setStampRankings(sortedByStamps);
          } catch (studentsError) {
            console.error('담임 학급 학생 정보 가져오기 실패:', studentsError);
            throw studentsError;
          }
        }

        // 교과 도장 순위 가져오기
        if (currentUser.subject) {
          const subjectStudentsRef = collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`);
          const subjectStudentsSnapshot = await getDocs(subjectStudentsRef);
          
          const subjectStudentsData = subjectStudentsSnapshot.docs.map(doc => {
            const data = doc.data();
            
            // stampCount 처리
            let stampCount = 0;
            if (typeof data.stampCount === 'object' && data.stampCount !== null) {
              stampCount = data.stampCount.count || 0;
            } else if (typeof data.stampCount === 'number') {
              stampCount = data.stampCount;
            }

            return {
              id: doc.id,
              name: data.name || '이름 없음',
              points: 0,
              stamps: stampCount
            };
          }) as RankData[];


          // 도장 기준으로 내림차순 정렬
          const sortedByStamps = [...subjectStudentsData].sort((a, b) => {
            const aStamps = isStampObject(a.stamps) ? a.stamps.count : a.stamps;
            const bStamps = isStampObject(b.stamps) ? b.stamps.count : b.stamps;
            return bStamps - aStamps;
          });
          setStampRankings(sortedByStamps);
        }

        // 학급/교과 아이템 승인: 담임·학급 ID가 없으면 경로가 `classes//...`가 되어 Firestore가 거부됨 (승인 직후 담임 미설정 교사 등)
        const schoolIdForPath = currentUser.schoolId;
        const classIdForPath = (currentUser.classId ?? '').toString().trim();
        const subjectKeyForPath = (currentUser.subjectId || currentUser.subject || '').toString().trim();

        let unsubscribeClassApprovals: () => void = () => {};
        if (schoolIdForPath && classIdForPath) {
          const classApprovalPath = `schools/${schoolIdForPath}/classes/${classIdForPath}/itemApprovals`;
          const classApprovalQuery = query(collection(db, classApprovalPath));
          unsubscribeClassApprovals = onSnapshot(classApprovalQuery, (snapshot) => {
            const classRequests = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                targetType: 'class',
                studentClassId: doc.data().studentClassId || currentUser.classId
              })) as ItemApprovalRequest[];

            const pendingClassRequests = classRequests
              .filter(req => req.status === 'pending')
              .sort((a, b) => {
                const aTime = a.createdAt?.getTime() || 0;
                const bTime = b.createdAt?.getTime() || 0;
                return bTime - aTime;
              });

            setApprovalRequests(prev => {
              const subjectRequests = prev.filter(req => req.targetType === 'subject');
              return [...pendingClassRequests, ...subjectRequests];
            });
          }, (error) => {
            console.error('학급 아이템 승인 요청 오류:', error);
          });
        }

        let unsubscribeSubjectApprovals: () => void = () => {};
        if (schoolIdForPath && subjectKeyForPath) {
          const subjectApprovalQuery = query(
            collection(db, `schools/${schoolIdForPath}/subjects/${subjectKeyForPath}/subjectitemApprovals`)
          );
          unsubscribeSubjectApprovals = onSnapshot(subjectApprovalQuery, (snapshot) => {
            const subjectRequests = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                targetType: 'subject',
                studentClassId: doc.data().studentClassId || currentUser.classId
              })) as ItemApprovalRequest[];

            const pendingSubjectRequests = subjectRequests
              .filter(req => req.status === 'pending')
              .sort((a, b) => {
                const aTime = a.createdAt?.getTime() || 0;
                const bTime = b.createdAt?.getTime() || 0;
                return bTime - aTime;
              });

            setApprovalRequests(prev => {
              const classRequests = prev.filter(req => req.targetType === 'class');
              return [...classRequests, ...pendingSubjectRequests];
            });
          }, (error) => {
            console.error('교과 아이템 승인 요청 오류:', error);
          });
        }

         // 일정 데이터 조회 (실시간 업데이트)
         let unsubscribeSchedules: (() => void) | null = null;
         if (isTeacher(currentUser) && currentUser.classId) {
           const teacher = currentUser as Teacher;
           const schedulesRef = collection(
             db,
             `schools/${teacher.schoolId}/classes/${teacher.classId}/schedules`
           );
           
           const q = query(
             schedulesRef,
             where('type', '==', 'class'),
             where('isActive', '==', true),
             orderBy('createdAt', 'desc')
           );
           
           unsubscribeSchedules = onSnapshot(
             q,
             (snapshot) => {
               const fetchedSchedules = snapshot.docs.map(doc => ({
                 id: doc.id,
                 ...doc.data()
               })) as Schedule[];
               
               setSchedules(fetchedSchedules);
             },
             (error) => {
               console.error('일정 데이터 조회 오류:', error);
             }
           );
         }

        return () => {
          unsubscribeClassApprovals();
          unsubscribeSubjectApprovals();
          if (unsubscribeSchedules) {
            unsubscribeSchedules();
          }
        };
      } catch (err) {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
        console.error('Error fetching data:', err);
        console.error('오류 상세 정보:', {
          message: err instanceof Error ? err.message : '알 수 없는 오류',
          code: (err as any)?.code,
          details: (err as any)?.details
        });
        console.error('전체 오류 객체:', err);
        console.error('오류 타입:', typeof err);
        console.error('오류 키들:', err && typeof err === 'object' ? Object.keys(err) : 'N/A');
        
        // Firebase 오류의 구체적인 정보 출력
        if (err && typeof err === 'object') {
          console.error('Firebase 오류 코드:', (err as any).code);
          console.error('Firebase 오류 메시지:', (err as any).message);
          console.error('Firebase 오류 세부사항:', (err as any).details);
          console.error('Firebase 오류 스택:', (err as any).stack);
          
          // JSON.stringify로 전체 객체 출력
          try {
            console.error('오류 객체 JSON:', JSON.stringify(err, null, 2));
          } catch (jsonError) {
            console.error('JSON 직렬화 실패:', jsonError);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // 알림 구독
  useEffect(() => {
    if (!currentUser?.uid) return;

    const unsubscribe = subscribeToUserNotifications(
      currentUser.uid,
      (notifications) => {
        setNotifications(notifications);
        setUnreadNotificationCount(notifications.filter(n => !n.isRead).length);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // 학급 사진 업로드 함수
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !isTeacher(currentUser)) return;

    const schoolId = currentUser.schoolId;
    const classId = resolveTeacherClassId(currentUser);
    if (!schoolId || !classId) {
      setError('학교 또는 담임 학급 정보가 없습니다. 설정에서 담임 학급을 저장한 뒤 다시 시도해 주세요.');
      return;
    }

    if (resolveTeacherPlan(currentUser.plan, currentUser.role) === 'free') {
      setError('사진 파일은 Firebase 저장소에 올라가 요금이 발생합니다. 무료 요금제는 글 공지를 사용해 주세요.');
      return;
    }

    // 사진이 이미 4장이면 업로드 불가
    if (classPhotos.length >= 4) {
      setError('최대 4장까지만 업로드할 수 있습니다.');
      return;
    }

    try {
      setUploadingPhoto(true);

      // Firebase Storage에 업로드 (파일명 특수문자·한글로 인한 실패 방지)
      const timestamp = Date.now();
      const extMatch = /\.[a-zA-Z0-9]{1,8}$/.exec(file.name);
      const ext =
        extMatch?.[0] ||
        (file.type === 'image/png' ? '.png' : file.type === 'image/webp' ? '.webp' : '.jpg');
      const safeFileName = `${timestamp}${ext}`;
      const storageRef = ref(storage, `schools/${schoolId}/classes/${classId}/photos/${safeFileName}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // 사진 배열에 추가
      const updatedPhotos = [...classPhotos, downloadURL];
      setClassPhotos(updatedPhotos);

      // Firestore에 사진 URL 저장
      const classRef = doc(db, `schools/${schoolId}/classes/${classId}`);
      await setDoc(classRef, {
        photos: updatedPhotos,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setSuccess('사진이 추가되었습니다.');
      setCurrentPhotoIndex(updatedPhotos.length - 1); // 새로 추가된 사진으로 이동
    } catch (err) {
      console.error('사진 업로드 중 오류:', err);
      const code = err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : '';
      if (code === 'storage/unauthorized') {
        setError('저장소 권한이 없습니다. Firebase Console에서 Storage 규칙을 배포했는지 확인해 주세요.');
      } else {
        setError(
          `사진 업로드에 실패했습니다.${err instanceof Error ? ` (${err.message})` : ''}`
        );
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  // 사진 삭제 함수
  const handlePhotoDelete = async () => {
    if (!currentUser || !isTeacher(currentUser) || classPhotos.length === 0) return;
    const schoolId = currentUser.schoolId;
    const classId = resolveTeacherClassId(currentUser);
    if (!schoolId || !classId) {
      setError('학교 또는 담임 학급 정보가 없습니다.');
      return;
    }

    if (!window.confirm('이 사진을 삭제하시겠습니까?')) return;

    try {
      const updatedPhotos = classPhotos.filter((_, idx) => idx !== currentPhotoIndex);
      setClassPhotos(updatedPhotos.length > 0 ? updatedPhotos : ['/images/class/ClassSampleImage.png']);
      
      // 현재 인덱스 조정
      if (currentPhotoIndex >= updatedPhotos.length) {
        setCurrentPhotoIndex(Math.max(0, updatedPhotos.length - 1));
      }

      // Firestore에 저장
      const classRef = doc(db, `schools/${schoolId}/classes/${classId}`);
      await setDoc(classRef, {
        photos: updatedPhotos.length > 0 ? updatedPhotos : [],
        updatedAt: serverTimestamp()
      }, { merge: true });

      setSuccess('사진이 삭제되었습니다.');
    } catch (err) {
      console.error('사진 삭제 중 오류:', err);
      setError('사진 삭제 중 오류가 발생했습니다.');
    }
  };

  // 사진 네비게이션
  const goToNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % classPhotos.length);
  };

  const goToPreviousPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + classPhotos.length) % classPhotos.length);
  };

  const goToPhoto = (index: number) => {
    setCurrentPhotoIndex(index);
  };

  // 알림 관련 핸들러 함수들
  const handleNotificationClick = () => {
    setOpenNotificationDialog(true);
  };

  const handleNotificationClose = () => {
    setOpenNotificationDialog(false);
  };

  const handleMarkNotificationAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(currentUser?.uid || '', notificationId);
    } catch (error) {
      console.error('알림 읽음 처리 중 오류:', error);
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    try {
      await markAllNotificationsAsRead(currentUser?.uid || '');
    } catch (error) {
      console.error('모든 알림 읽음 처리 중 오류:', error);
    }
  };

  // 학생 아이템 조회 함수
  const handleViewStudentItems = async (studentId: string, studentName: string) => {
    setSelectedStudentForItems({ id: studentId, name: studentName });
    setOpenStudentItemsDialog(true);

    try {
      // 학생 정보 조회
      const studentQuery = query(
        collectionGroup(db, 'students'),
        where('uid', '==', studentId),
        where('schoolId', '==', currentUser?.schoolId)
      );
      
      const studentSnapshot = await getDocs(studentQuery);
      
      if (!studentSnapshot.empty) {
        const studentData = studentSnapshot.docs[0].data();
        const items = studentData.items || {};
        
        // items 객체를 배열로 변환 (used 상태 제외)
        const itemsArray = Object.entries(items)
          .map(([key, value]: [string, any]) => ({
            id: key,
            name: value.name || '알 수 없는 아이템',
            status: value.status || 'available',
            type: value.type || 'class',
            description: value.description || '',
            purchasedAt: value.purchasedAt?.toDate?.() || null
          }))
          .filter(item => item.status !== 'used');  // used 상태 제외
        
        setStudentItems(itemsArray);
      } else {
        setStudentItems([]);
      }
    } catch (err) {
      console.error('학생 아이템 조회 중 오류:', err);
      setError('학생 아이템을 불러오는 중 오류가 발생했습니다.');
    }
  };

  const handleHomeroomClassChange = async (grade: number, classNumber: number) => {
    try {
      if (!currentUser || !currentUser.schoolId) {
        return;
      }

      // 새로운 담임 학급 설정
      const newHomeroomClass = { 
        grade: Number(grade), 
        class: Number(classNumber) 
      };
      
      const batch = writeBatch(db);
      
      // 교사 정보 업데이트
      if (!currentUser?.uid) {
        throw new Error('교사 정보가 없습니다.');
      }
      const teacherRef = doc(db, 'teachers', currentUser.uid);

      batch.update(teacherRef, {
        homeroomClass: newHomeroomClass,
        updatedAt: serverTimestamp()
      });

      // 학급 문서에 필드 추가
      const classRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}`);
      batch.set(classRef, {
        grade: Number(grade),
        class: Number(classNumber),
        teacherId: currentUser.uid,
        teacherName: currentUser.name,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      await batch.commit();

      // 현재 사용자 정보 업데이트
      setCurrentUser((prev: User | null) => {
        if (!prev || prev.role !== 'teacher') {
          return null;
        }
        return {
          ...prev,
          homeroomClass: newHomeroomClass
        };
      });
    } catch (err) {
      console.error('담임 학급 변경 중 오류:', err);
      setError('담임 학급을 변경하는 중 오류가 발생했습니다.');
    }
  };

  const handleHomeroomClassDelete = async () => {
    try {
      const teacher = currentUser as Teacher;
      if (!teacher || !teacher.homeroomClass) return;

      const batch = writeBatch(db);
      
      // 교사 문서에서 담임 반 정보 삭제
      const teacherRef = doc(db, 'teachers', teacher.uid);
      batch.update(teacherRef, {
        homeroomClass: null,
        classId: null,
        updatedAt: new Date()
      });

      // 학급 문서에서 담임 교사 정보 삭제
      const classRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}`);
      batch.update(classRef, {
        teacherId: null,
        teacherName: null,
        updatedAt: serverTimestamp()
      });

      await batch.commit();
      
      // 로컬 상태 업데이트
      setCurrentUser(prev => {
        if (!prev) return null;
        return {
          ...prev,
          homeroomClass: undefined,
          classId: undefined,
          updatedAt: new Date()
        } as Teacher;
      });

      setError('담임 반이 삭제되었습니다.');
    } catch (error) {
      console.error('담임 반 삭제 중 오류:', error);
      setError('담임 반을 삭제하는데 실패했습니다.');
    }
  };
  
  // 학생 클릭 시 상세 정보 다이얼로그 열기
  const handleStudentClick = async (student: RankData) => {
    setSelectedStudent(student);
    setOpenStudentDialog(true);
    setLoadingHistory(true);
    
    try {
      if (!currentUser?.schoolId || !currentUser?.classId) {
        console.error('학교 ID 또는 반 ID가 없습니다:', { schoolId: currentUser?.schoolId, classId: currentUser?.classId });
        setError('학교 또는 반 정보를 찾을 수 없습니다.');
        return;
      }

      // 포인트 내역 가져오기 (studentId 필드 사용)
      const historyRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/pointHistories`);
      const q = query(historyRef, where('studentId', '==', student.id), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const history = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate()
        };
      }) as PointHistory[];
      
      setPointHistory(history);
    } catch (err) {
      console.error('포인트 내역을 불러오는 중 오류:', err);
      setError('포인트 내역을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // 탭 변경 처리
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 승인 요청 다이얼로그 열기
  const handleOpenApprovalDialog = (request: ItemApprovalRequest) => {
    setSelectedRequest(request);
    setOpenApprovalDialog(true);
  };

  // 아이템이 없는 승인 요청 삭제
  const handleDeleteItemNotFoundRequest = async () => {
    if (!itemNotFoundRequest || !currentUser?.schoolId) return;
    
    try {
      const requestPath = itemNotFoundRequest.targetType === 'class' 
        ? `schools/${currentUser.schoolId}/classes/${currentUser.classId}/itemApprovals/${itemNotFoundRequest.id}`
        : `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/subjectitemApprovals/${itemNotFoundRequest.id}`;
      
      const requestRef = doc(db, requestPath);
      await updateDoc(requestRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
        reason: '아이템이 더 이상 존재하지 않음',
        cancelledBy: currentUser.uid,
        cancelledAt: serverTimestamp()
      });

      setSuccess('승인 요청이 삭제되었습니다.');
      
      // 승인 요청 목록에서 제거
      setApprovalRequests(prev => prev.filter(req => req.id !== itemNotFoundRequest.id));
      
    } catch (error) {
      console.error('승인 요청 삭제 오류:', error);
      setError('승인 요청 삭제 중 오류가 발생했습니다.');
    } finally {
      setOpenItemNotFoundDialog(false);
      setItemNotFoundRequest(null);
      setOpenApprovalDialog(false); // 아이템 사용 다이얼로그도 닫기
      setSelectedRequest(null);
    }
  };

  // 승인 요청 처리
  const handleApprovalRequest = async (request: ItemApprovalRequest, approved: boolean, reason?: string) => {
    if (!currentUser?.schoolId || !isTeacher(currentUser)) {
      console.error('교사 권한이 없거나 학교 ID가 없습니다.');
      return;
    }
    
    if (!request.studentClassId) {
      console.error('학생의 학급 정보가 없습니다.');
      setError('학생의 학급 정보가 없습니다.');
      return;
    }

    try {
      const batch = writeBatch(db);
      
      // 승인 요청 상태 업데이트
      const requestPath = request.targetType === 'class' 
        ? `schools/${currentUser.schoolId}/classes/${currentUser.classId}/itemApprovals/${request.id}`
        : `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/subjectitemApprovals/${request.id}`;

      const requestRef = doc(db, requestPath);
      batch.update(requestRef, {
        status: approved ? 'approved' : 'rejected',
        updatedAt: serverTimestamp(),
        reason: reason || null,
        approvedBy: currentUser.uid,
        approvedAt: serverTimestamp()
      });

      // 학생 정보를 찾아서 아이템 상태 처리
        const studentQuery = query(
          collectionGroup(db, 'students'),
          where('uid', '==', request.studentId),
          where('schoolId', '==', currentUser.schoolId)
        );
        
        const studentSnapshot = await getDocs(studentQuery);

        if (!studentSnapshot.empty) {
          const studentDoc = studentSnapshot.docs[0];
          const studentData = studentDoc.data();
          const studentItems = studentData.items || {};
          // 고유키별로 itemId가 일치하는 항목을 찾는다
          const allItems = Object.entries(studentItems) as [string, any][];
          const matching = allItems.filter(([key]) =>
            key === request.itemId  // uniqueKey와 직접 비교
          );
          if (matching.length === 0) {
            console.error('아이템 정보가 없습니다.');
            // 아이템이 없는 경우 다이얼로그 표시
            setItemNotFoundRequest(request);
            setOpenItemNotFoundDialog(true);
            return;
          } else {
            const [useKey] = matching[0];
          
          if (approved) {
            // 승인된 경우: 아이템을 삭제하고 사용 권한 부여
            batch.update(studentDoc.ref, {
              [`items.${useKey}`]: deleteField()
            });
            
            // 아이템 사용 권한 부여 (승인 요청 ID를 포함하여 고유성 보장)
            const usagePath = request.targetType === 'class'
              ? `schools/${currentUser.schoolId}/classes/${currentUser.classId}/itemUsages/${request.id}`
              : `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/subjectitemUsages/${request.id}`;
            const itemUsageRef = doc(db, usagePath);
            batch.set(itemUsageRef, {
              studentId: request.studentId,
              itemId: request.itemId,
              approvalRequestId: request.id,
              approvedAt: serverTimestamp(),
              approvedBy: currentUser.uid,
              status: 'active'
            });
          } else {
            // 거절된 경우: 아이템 상태를 available로 되돌리기
            batch.update(studentDoc.ref, {
              [`items.${useKey}.status`]: 'available'
            });
          }
          }
        } else {
          console.error('학생 정보를 찾을 수 없습니다.');
          setError('학생 정보를 찾을 수 없습니다.');
          return;
      }

      await batch.commit();

      // 학생에게 결과 알림 전송
      try {
        const notificationMessage = approved
          ? `${request.itemName} 아이템 사용이 승인되었습니다.`
          : `${request.itemName} 아이템 사용이 거절되었습니다.${reason ? ` (사유: ${reason})` : ''}`;
        await createItemNotification(
          request.studentId,
          approved ? '아이템 사용 승인 완료' : '아이템 사용 거절 안내',
          notificationMessage,
          currentUser.schoolId,
          request.targetType === 'class' ? request.studentClassId : undefined,
          'medium',
          '/class/student/ClassMyItems',
          {
            itemName: request.itemName,
            approvalRequestId: request.id,
            result: approved ? 'approved' : 'rejected'
          }
        );
      } catch (notificationError) {
        console.error('학생 알림 전송 중 오류:', notificationError);
      }

      setOpenApprovalDialog(false);
    } catch (err) {
      console.error('승인 처리 중 오류:', err);
      setError('승인 처리 중 오류가 발생했습니다.');
    }
  };

  const fetchNotices = useCallback(async () => {
    const cid = (currentUser?.classId ?? '').toString().trim();
    if (!currentUser?.schoolId || !cid) {
      // 담임 학급 미설정 교사는 classId 없음 — 정상
      return;
    }

    try {
      const noticesRef = collection(
        db,
        `schools/${currentUser.schoolId}/classes/${cid}/notices`
      );
      const noticesQuery = query(noticesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(noticesQuery);
      const fetchedNotices: AddNoticeItem[] = snapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          content: data.content || '',
          startDate: data.startDate || '',
          endDate: data.endDate || '',
          status: data.status === '완료' ? '완료' : '진행중',
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
          createdBy: data.createdBy,
          createdByName: data.createdByName
        };
      });
      setNoticeItems(fetchedNotices);
    } catch (fetchError) {
      console.error('알림 목록 조회 중 오류:', fetchError);
      setError('알림 목록을 불러오는 중 오류가 발생했습니다.');
    }
  }, [currentUser?.schoolId, currentUser?.classId]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleOpenAddNoticeDialog = () => {
    fetchNotices();
    setIsAddNoticeDialogOpen(true);
  };

  const handleDeleteNotices = async (noticeIds: string[]) => {
    if (!currentUser?.schoolId || !currentUser?.classId) {
      const errorMessage = '학교 또는 학급 정보를 찾을 수 없습니다.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    const pathBase = `schools/${currentUser.schoolId}/classes/${currentUser.classId}/notices`;

    try {
      await Promise.all(
        noticeIds.map((noticeId) => deleteDoc(doc(db, `${pathBase}/${noticeId}`)))
      );
      setSuccess('선택한 알림이 삭제되었습니다.');
      await fetchNotices();
    } catch (err) {
      console.error('알림 삭제 오류:', err);
      const errorMessage = '알림을 삭제하는 중 오류가 발생했습니다.';
      setError(errorMessage);
      throw err;
    }
  };

  const handleAddNotice = async (notice: NoticeFormValues) => {
    if (!currentUser?.schoolId || !currentUser?.classId) {
      const errorMessage = '학교 또는 학급 정보를 찾을 수 없습니다.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    const noticesRef = collection(
      db,
      `schools/${currentUser.schoolId}/classes/${currentUser.classId}/notices`
    );

    try {
      const docRef = await addDoc(noticesRef, {
        ...notice,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        createdByName: currentUser.name
      });
      setSuccess('알림이 등록되었습니다.');
      await fetchNotices();
    } catch (err) {
      console.error('알림 등록 오류:', err);
      const errorMessage = '알림을 등록하는 중 오류가 발생했습니다.';
      setError(errorMessage);
      throw err;
    }
  };

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  // 교시 전환 감지를 위해 매분 정각에 갱신
  const [timetableNow, setTimetableNow] = useState(() => new Date());
  useEffect(() => {
    let intervalId: number | undefined;
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000) + 50;
    const timeoutId = window.setTimeout(() => {
      setTimetableNow(new Date());
      intervalId = window.setInterval(() => setTimetableNow(new Date()), 60_000);
    }, msUntilNextMinute);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, []);

  const currentTimetableStatus = useMemo(
    () => getCurrentTimetableStatus(timetableData.entries, timetableNow),
    [timetableData.entries, timetableNow]
  );

  const activeNotices = useMemo(() => {
     const filtered = noticeItems.filter((notice) => {
       if (!notice.startDate || !notice.endDate) return false;
 
       const parseDate = (value: string) => {
         const normalized = value.replace(/\./g, '-');
         const [year, month, day] = normalized.split('-').map((part) => Number(part));
         if (!year || !month || !day) return null;
         return new Date(year, month - 1, day);
       };
 
       const start = parseDate(notice.startDate);
       const end = parseDate(notice.endDate);
       if (!start || !end) return false;
       return start <= today && today <= end;
     });
     return filtered;
   }, [noticeItems, today]);

  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);
  const [noticeStage, setNoticeStage] = useState<'enter' | 'exit'>('enter');

  useEffect(() => {
    setCurrentNoticeIndex(0);
  }, [activeNotices.length]);

  useEffect(() => {
    if (activeNotices.length <= 1) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const intervalId = setInterval(() => {
      setNoticeStage('exit');
      timeoutId = setTimeout(() => {
        setCurrentNoticeIndex((prev) => (prev + 1) % activeNotices.length);
        setNoticeStage('enter');
      }, 300);
    }, 5000);

    return () => {
      clearInterval(intervalId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeNotices.length]);

  useEffect(() => {
    setNoticeStage('enter');
  }, [currentNoticeIndex]);

  const currentNoticeContent = activeNotices.length > 0
    ? activeNotices[currentNoticeIndex]?.content ?? ''
    : '현재 노출 중인 알림이 없습니다. 새로운 알림을 등록해 주세요.';

  if (!currentUser || (!isTeacher(currentUser) && !isSuperAdmin(currentUser))) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        {loading ? (
          <div>로딩 중...</div>
        ) : (
          <div style={{ 
            color: 'red', 
            padding: '20px', 
            border: '1px solid red', 
            borderRadius: '4px' 
          }}>
            교사 권한이 필요합니다.
          </div>
        )}
      </div>
    );
  }

  /*여기서부터 UI 영역*/  
  return (
    <div data-page="class-teacher-home" className={styles.root}>
      {/* 학교 로고 및 학교 이름 표시*/}
      <div data-section="header" data-block="school-info" className={styles.headerSchool}>
        {schoolInfo?.logo ? (
          <img 
            src={schoolInfo.logo} 
            alt={`${schoolInfo.name} 로고`}
            className={styles.schoolLogoImg}
            onError={(e) => {
              // 이미지 로딩 실패 시 기본 아이콘으로 대체
              e.currentTarget.style.display = 'none';
              const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
              if (nextElement) {
                nextElement.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div className={styles.schoolLogoFallback} style={{ display: schoolInfo?.logo ? 'none' : 'flex' }}>
          🏫
        </div>
        <div className={styles.schoolName}>
            {schoolInfo?.name || '학교 정보를 불러오는 중...'}
        </div>
      </div>
      {/* 인사말 헤더*/}
      <div data-section="header" data-block="greeting" className={styles.greeting}>
        <h1 className={styles.greetingTitle}>만나서 반갑습니다, {currentUser.name}님</h1>
      </div>
      {/* 중요 알림 창*/}
      <div data-section="notice" data-block="notice-bar" className={styles.noticeBar}>
        <div className={styles.noticeBox}>
          <div className={styles.noticeBadge}>
            <span className={styles.noticeBadgeLabel}>중요</span>
          </div>
          <p
            className={styles.noticeMessage}
            style={{
              transform: noticeStage === 'enter' ? 'translateY(0)' : 'translateY(-0.5rem)',
              opacity: noticeStage === 'enter' ? 1 : 0,
            }}
          >
            {currentNoticeContent}
          </p>
        </div>
        <div className={styles.noticeBarButton}>
          <Button
            width="100%"
            height="100%"
            size="medium"
            variant="submit"
            onClick={handleOpenAddNoticeDialog}
            leftIcon={<PlusCircleIcon width={14} height={13} color="white" />}
          >
            알림 등록
          </Button>
        </div>
      </div>
      
      {/* 대시보드 전체 영역*/}
      <div data-section="dashboard" className={styles.dashboard}>
        {/* 반, 일정, 포인트, 사용 승인 요청 영역*/}
        <div data-block="main-column" className={styles.mainColumn}>
          <div data-block="top-row" className={styles.topRow}>
            {/* 반 정보 카드 - 항상 표시 (309px 높이에 맞춤, border-box로 패딩 포함) */}
            <div className={styles.classInfoCardWrap}>
          <Card data-block="class-info"
              width="100%"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={10}
              style={{ boxSizing: 'border-box', display: 'inline-flex', minHeight: '100%', overflow: 'hidden' }}
            >
          {/* 담임 학급, 날짜 정보 영역, 학년, 학급 인원, 사진 전체 영역 설정*/}
            <div className={styles.classInfoInner}>
                {/* 담임 학급, 날짜 정보 영역 */}
              <div className={styles.classInfoHeader}>
                  {/* 학급 정보 텍스트 박스 */}
                  <div className={styles.classInfoText}>
                      {isMobile ? (
                        // 모바일: 학년과 반을 합쳐서 표시
                        <div className={styles.classGradeTitleMobile}>
                          {isTeacher(currentUser) && currentUser.homeroomClass?.grade && currentUser.homeroomClass?.class
                            ? `${currentUser.homeroomClass.grade}학년 ${currentUser.homeroomClass.class}반`
                            : isTeacher(currentUser) && currentUser.homeroomClass?.class
                              ? `${currentUser.homeroomClass.class}반`
                              : '-'}
                        </div>
                      ) : (
                        // 데스크톱: 기존 형식 유지
                        <>
                          <div className={styles.classGradeTitleDesktop}>
                            {isTeacher(currentUser) && currentUser.homeroomClass?.class}반
                          </div>
                          <div className={styles.classGradeSubDesktop}>
                            {semesterDates 
                              ? formatSemesterPeriodLabel(semesterDates.startDate, semesterDates.endDate)
                              : '2025.08.22-12.23'}
                          </div>
                        </>
                      )}
                    </div>
                  <div className={styles.classMetaRow}>
                    {isMobile ? (
                      // 모바일: 기간과 인원을 한 줄에 배치
                      <div style={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-assistive, #989898)', 
                          fontSize: '16px',
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '24px',
                          whiteSpace: 'nowrap',
                          margin: 0
                        }}>
                          {semesterDates 
                            ? formatSemesterPeriodLabel(semesterDates.startDate, semesterDates.endDate)
                            : '2025.08.22-12.23'}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <div style={{
                            color: 'var(--Color-text-assistive, #989898)', 
                            fontSize: '16px',
                            fontFamily: 'Pretendard', 
                            fontWeight: '500', 
                            lineHeight: '24px',
                            wordWrap: 'break-word',
                            margin: 0
                          }}>
                            학급 인원
                          </div>
                          <div style={{
                            color: 'var(--Color-text-normal, #161616)', 
                            fontSize: '16px',
                            fontFamily: 'Pretendard', 
                            fontWeight: '500', 
                            lineHeight: '24px',
                            wordWrap: 'break-word',
                            margin: 0
                          }}>
                            {`${studentCount}명`}
                          </div>
                        </div>
                      </div>
                    ) : (
                      // 데스크톱: 기존 형식 유지
                      <>
                        <div className={styles.classMetaItem}>
                          <div className={styles.classMetaLabel}>학년</div>
                          <div className={styles.classMetaValue}>{isTeacher(currentUser) && currentUser.homeroomClass?.grade}학년</div>
                        </div>
                        <div className={styles.classMetaItem}>
                          <div className={styles.classMetaLabel}>학급 인원</div>
                          <div className={styles.classMetaValue}>{`${studentCount}명`}</div>
                        </div>
                      </>
                    )}
                </div>

                                </div>
               {/* 사진 전체 영역 */}
               <div data-block="class-photos" className={styles.classPhotosWrap}>
                   {/* 사진 영역 */}
                  <div className={styles.photoContainer}
                    onClick={() => {
                      if (!isTeacher(currentUser) || resolveTeacherPlan(currentUser.plan, currentUser.role) === 'free') {
                        setError('사진 파일은 Firebase 저장소에 올라가 요금이 발생합니다. 무료 요금제는 글 공지를 사용해 주세요.');
                        return;
                      }
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => handlePhotoUpload(e as any);
                      input.click();
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                      // 삭제 버튼 표시
                      const deleteBtn = e.currentTarget.querySelector('.delete-button') as HTMLElement;
                      if (deleteBtn) deleteBtn.style.opacity = '1';
                      // 안내 텍스트 표시
                      const uploadHint = e.currentTarget.querySelector('.upload-hint') as HTMLElement;
                      if (uploadHint) uploadHint.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                      // 삭제 버튼 숨김
                      const deleteBtn = e.currentTarget.querySelector('.delete-button') as HTMLElement;
                      if (deleteBtn) deleteBtn.style.opacity = '0';
                      // 안내 텍스트 숨김
                      const uploadHint = e.currentTarget.querySelector('.upload-hint') as HTMLElement;
                      if (uploadHint) uploadHint.style.opacity = '0';
                    }}
                    >
                    <img 
                      style={{
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRadius: '12px',
                        transition: 'opacity 0.2s'
                      }} 
                      src={classPhotos[currentPhotoIndex] || '/images/class/ClassSampleImage.png'}
                      alt="학급 사진"
                    />
                    {/* 업로드 중 표시 */}
                    {uploadingPhoto && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex', 
                alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        업로드 중...
                            </div>
                    )}
                    {/* 삭제 버튼 (기본 이미지가 아닐 때만 표시, 호버 시에만 나타남) */}
                    {!uploadingPhoto && !classPhotos[currentPhotoIndex]?.includes('ClassSampleImage.png') && (
                      <div 
                        className="delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePhotoDelete();
                        }}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          width: '16px',
                          height: '16px',
                          background: 'rgba(255, 56, 56, 0.9)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          opacity: 0,
                          transition: 'all 0.2s',
                          zIndex: 10
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 56, 56, 1)';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 56, 56, 0.9)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <img 
                          src="/images/icons/closeIcon.svg" 
                          alt="삭제"
                          style={{
                            width: '10px',
                            height: '10px',
                            filter: 'invert(1)'
                          }}
                        />
                        </div>
                    )}
                    {/* 호버 시 안내 텍스트 */}
                    {!uploadingPhoto && classPhotos.length < 4 && (
                      <div 
                        className="upload-hint"
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          fontSize: '10px',
                          textAlign: 'center',
                          padding: '4px',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          pointerEvents: 'none'
                        }}
                      >
                        {isTeacher(currentUser) && resolveTeacherPlan(currentUser.plan, currentUser.role) === 'free'
                          ? '무료 요금제는 글 공지를 사용해 주세요'
                          : `클릭해서 사진 추가 (${classPhotos.length}/4)`}
                    </div>
                    )}
                </div>
                  {/* 슬라이더 인디케이터 */}
                  <div className={styles.photoSlider}>
                      {[0, 1, 2, 3].map((index) => (
                        <div 
                          key={index}
                          className={styles.photoDot}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (index < classPhotos.length) {
                              goToPhoto(index);
                            }
                          }}
                          style={{
                            background: index === currentPhotoIndex && index < classPhotos.length 
                              ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                              : index < classPhotos.length 
                                ? '#989898'
                                : '#D9D9D9', 
                            cursor: index < classPhotos.length ? 'pointer' : 'default',
                          }} 
                        />
                      ))}
                </div>
            </div>
        </div>
          </Card>
            </div>
            {/* 일정 정보 카드 - 모바일에서 숨김 */}
            {!isMobile && (
          <Card data-block="schedule"
              width={isMobile ? "100%" : "628px"}
              height={isMobile ? "auto" : "309px"}
              padding="16px 20px"
              flexDirection={isMobile ? "column" : "row"}
              alignItems="flex-start"
              gap={isMobile ? "16px" : "27px"}
              style={{ boxSizing: 'border-box', minHeight: isMobile ? "auto" : "309px" }}
            >
              <div data-block="schedule-content-area" style={{
                flex: isMobile ? 'none' : '0 0 233px', 
                width: isMobile ? '100%' : 'auto',
                height: isMobile ? 'auto' : '100%',
                minHeight: isMobile ? 'auto' : 0,
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'flex-start', 
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: '16px'
              }}>
                  <div data-block="schedule-header" style={{
                    width: '100%', 
                    height: '50px', 
                    display: 'flex', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    flexDirection: 'column',
                    gap: '2px'}}>
                    <div style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-normal, #161616)',
                      fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                      fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '150%', /* 27px */
                      letterSpacing: '-0.18px',
                      margin: 0
                    }}>
                      {new Date().getFullYear()}년 {new Date().getMonth() + 1}월
</div>  
                    <div data-block="schedule-subheader" style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                      fontSize: 'var(--Font-Font-size-Body-3, 14px)',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '150%',
                      margin: 0
                    }}>
                      {/* 3월 ~ 7월 : 1학기, 9월 ~ 2월 : 2학기 */}
                      {new Date().getFullYear()}년 {new Date().getMonth() + 1 >= 3 && new Date().getMonth() + 1 <= 7 ? '1학기' : '2학기'}
                    </div>
                </div>
                  {/* 일정 내용 영역 - box-sizing으로 211px에 패딩 포함, 세로 넘침 방지 */}
      <div style={{ 
        boxSizing: 'border-box',
        display: 'flex',
                    height: '211px',
                    padding: '16px',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '16px',
                    alignSelf: 'stretch',
                    borderRadius: '12px',
                    background: 'var(--Color-background-sub, #F8F8F8)',
                  }}>
                    {/* 상단 일, 요일, 일정 개수 내용 영역 */}
                    <div style={{
                      width: '100%',
                      height: '43px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: '4px'
                    }}>
                      {/* 일정 내용 */}
                      <div style={{
                display: 'flex', 
                alignItems: 'center',
                        gap: '7px',
                        height: '21px'
                      }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          padding: '1.6px 2.27px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img 
                            src={'/images/icons/calendar.svg'} 
                            alt="달력 아이콘"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
      </div>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: '600',
                          lineHeight: '150%',
                          margin: 0
                        }}>
                          {new Date().getDate()}일 {['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]}요일 일정
                        </span>
        </div>
</div>  
                      {/* 하단 일정 내용 영역 */}
      <div style={{ 
                        width: '100%',
                        height: '168px',
        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '16px', 
                        overflow: 'auto'
                      }}>
                        {/* 일정 목록 */}
                        {(() => {
                          const today = new Date();
                          const todaySchedules = schedules.filter(schedule => {
                            return isScheduleOnDate(schedule, today.getDate(), today.getFullYear(), today.getMonth());
                          });
                          
                          if (todaySchedules.length > 0) {
                            return todaySchedules.map((schedule) => (
                            <div 
                              key={schedule.id}
            style={{ 
                                width: '100%',
                                minHeight: '42px',
              display: 'flex',
              flexDirection: 'column',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                gap: '4px',
                                padding: '8px 0px'
                              }}
                            >
                              <div style={{
                                width: '100%',
                                height: '20px',
                display: 'flex', 
                                justifyContent: 'flex-start',
                alignItems: 'center',
                                gap: '9px'
                              }}>
                                <img src={'/images/icons/circleIcon.svg'} alt="원 아이콘"/>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                  fontSize: '13px',
                                  fontStyle: 'normal',
                                  fontWeight: '600',
                                  lineHeight: '150%',
                                  margin: 0
                                }}>
                                  {schedule.title}
                                </span>
                                {schedule.dateOption === 'custom' && schedule.startDate && (
                                  <span style={{
                                    color: 'var(--Color-text-assistive, #989898)',
                                    fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                    fontSize: '12px',
                                    fontWeight: '400',
                                    marginLeft: 'auto'
                                  }}>
                                    {schedule.startDate.toDate().toLocaleDateString('ko-KR')} ~ {schedule.endDate?.toDate().toLocaleDateString('ko-KR')}
                                  </span>
                                )}
                                {schedule.dateOption === 'period' && schedule.period && (
                                  <span style={{
                                    color: 'var(--Color-text-assistive, #989898)',
                                    fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                    fontSize: '12px',
                                    fontWeight: '400',
                                    marginLeft: 'auto'
                                  }}>
                                    {schedule.period}교시
                                  </span>
                                )}
                              </div>
                              {schedule.description && (
                              <p style={{
                                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                                fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                fontSize: 'var(--Font-Font-size-Label-2, 12px)',
                                fontStyle: 'normal',
                                fontWeight: '500',
                                lineHeight: '150%',
                                margin: 0
                              }}>
                                {schedule.description}
                              </p>
                              )}
                            </div>
                            ));
                          } else {
                            return (
                          <div style={{
                            width: '100%',
                            height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                            color: 'var(--Color-text-assistive, #989898)',
                            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                            fontSize: '12px'
                          }}>
                            오늘 일정이 없습니다.
              </div>
                            );
                          }
                        })()}
      </div>

                  </div>
              </div>
              {/* 달력 */}
      <div data-block="calendar-area" style={{ 
                width: isMobile ? '100%' : '328px',
                height: isMobile ? 'auto' : '277px',
                minHeight: isMobile ? 'auto' : '277px',
                display: 'flex', 
                justifyContent: 'flex-start',
                alignItems: 'center',
                flexDirection: 'column',
                padding: isMobile ? '12px' : '17px 8px 4px 8px',
                gap: '12px', 
                boxSizing: 'border-box',
              }}>
              <div data-block="calendar-header" style={{ 
                  width: '100%',
                  height: '20px',
                  flexShrink: '0',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  columnGap: '36px',
                  alignItems: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%',
                  padding: '0 6px',
                }}>
                  <span style={{ textAlign: 'center' }}>일</span>
                  <span style={{ textAlign: 'center' }}>월</span>
                  <span style={{ textAlign: 'center' }}>화</span>
                  <span style={{ textAlign: 'center' }}>수</span>
                  <span style={{ textAlign: 'center' }}>목</span>
                  <span style={{ textAlign: 'center' }}>금</span>
                  <span style={{ textAlign: 'center' }}>토</span>
            </div>
                {/* 달력 그리드 */}
            <div style={{ 
                  width: '100%',
                  height: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  columnGap: '24px',
                  rowGap: '4px'
                }}>
                  {generateCalendar().map((day, index) => (
                    <div 
                      key={index}
            style={{ 
              display: 'flex',
                        width: '24px',
                        height: '34px',
              flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '6px'
                      }}
                    >
                      {/* 날짜 기입 영역 */}
                  <div data-block="calendar-date" style={{ 
                        display: 'flex', 
                        boxSizing: 'border-box',
                        width: '100%',
                        height: '24px',
                        padding: '3px',
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        alignItems: 'center',
                gap: '8px', 
                        flexShrink: '0',
                        aspectRatio: '1/1',   
                        color: day.isCurrentMonth 
                          ? (day.isToday ? 'var(--Color-text-inverse, #FFF)' : 'var(--Color-text-normal, #161616)')
                          : 'var(--Color-text-assistive, #989898)',
                        backgroundColor: day.isToday ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                        borderRadius: day.isToday ? '50%' : '0',
                        textAlign: 'center',
                        fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                        fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '150%'
                      }}>
                        {day.date}
                </div>
                      {/* 일정 아이콘 */}
                      <div style={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {day.isCurrentMonth && schedules.some(schedule => {
                          const calendarYear = new Date().getFullYear();
                          const calendarMonth = new Date().getMonth();
                          return isScheduleOnDate(schedule, day.date, calendarYear, calendarMonth);
                        }) && (
                          <img src={'/images/icons/circleSky.svg'} alt="일정 있음"/>
                        )}
                </div>
                    </div>
                  ))}
                    </div>
                    </div>
            </Card>
          )}
          </div>
          {/* 포인트 및 아이템 승인 요청 영역 - 모바일에서 숨김 */}
          {!isMobile && (
          <div data-block="point-and-approval-row" style={{
            boxSizing: 'border-box',
            width: '100%',
            height: isMobile ? 'auto' : '309px',
            minHeight: isMobile ? 'auto' : '309px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '25px'
          }}>
            {/* 포인트 */}
            <Card data-block="point-ranking"
              width={isMobile ? "100%" : "302px"}
              height={isMobile ? "auto" : "100%"}
              padding="16px 20px"
              alignItems="flex-start"
              gap={10}
              style={{ display: 'inline-flex', minHeight: isMobile ? "auto" : "100%", boxSizing: 'border-box' }}
            >
              <div data-block="point-ranking-content" style={{
                boxSizing: 'border-box',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '28px'
              }}>
                <div data-block="point-ranking-header" style={{
                  width: '100%',
                  height: '27px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, pretendard)',
                  fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '150%', /* 27px */
                  letterSpacing: '-0.18px',
                  margin: 0
                }}>
                  우리반 포인트 현황
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  overflow: 'hidden'
                }}>
                  {/* 포인트 표 제목 영역*/}
                <div data-block="point-ranking-table-header" style={{
                  width: '100%',
                  height: '28px',
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '4px 8px',
                  gap: '12px',
                  boxSizing: 'border-box',
                  borderRadius: 'var(--common-8, 8px)',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%', /* 19.5px */
                }}> 
                  <div data-block="point-ranking-table-header-rank" style={{
                    width: '40px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                  }}>
                    순위
              </div>
                  <div data-block="point-ranking-table-header-name" style={{
                    width: '90px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                  }}>
                    이름
            </div>
                  <div data-block="point-ranking-table-header-number" style={{
                    width: '29px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                  }}>
                    번호
          </div>
                  <div data-block="point-ranking-table-header-point" style={{
                    width: '47px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                  }}>
                    포인트
        </div>
                </div>
                {/* 포인트 표 내용 영역*/}
        <div data-block="point-ranking-table-content" style={{ 
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxSizing: 'border-box',
        }}>
                    {pointRankings.map((student, index) => (
          <div 
                        key={index}
            data-block="point-ranking-table-content-item" style={{ 
                         width: '100%',
                         height: '38px',
                         padding: '8px',
                         borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                         gap: '12px',
              display: 'flex',
                         flexDirection: 'row',
                         boxSizing: 'border-box',
                       }}
                     >
                         {index < 3 ? (
                           <div data-block="point-ranking-table-content-item-rank" style={{
                  width: '40px',
                display: 'flex', 
                             height: '22px',
                             boxSizing: 'border-box',
                             padding: '2px 12px',
                             justifyContent: 'center',
                alignItems: 'center',
                             gap: '4px', 
                             borderRadius: '8px',
                             background: index === 0 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 
                                        index === 1 ? 'var(--Color-fill-brand-color-2, #FF9F5E)' : 
                                        'var(--Color-fill-brand-color-3, #FFD2B5)'
                           }}>
                             <div data-block="point-ranking-table-content-item-rank-text" style={{
                               height: '18px',
                               boxSizing: 'border-box',
                               width: 'auto',
                               minWidth: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                               color: 'var(--Color-text-inverse, #FFF)',
                               fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                               fontSize: 'var(--Font-Font-size-Label-2, 12px)',
                               margin: '0px',
                               fontStyle: 'normal',
                               fontWeight: '500',
                               lineHeight: '150%',
                               whiteSpace: 'nowrap'
                             }}>
                               {index + 1}위
                             </div>
                           </div>
                         ) : (
                           <div data-block="point-ranking-table-content-item-rank" style={{
                             width: '40px',
                             display: 'flex',
                             height: '22px',
                             boxSizing: 'border-box',
                             padding: '2px 12px',
                             justifyContent: 'center',
                             alignItems: 'center',
                             gap: '4px',
                             color: 'var(--Color-text-normal, #161616)',
                             fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                             fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                             fontStyle: 'normal',
                             fontWeight: '400',
                             lineHeight: '150%',
                             whiteSpace: 'nowrap'
                           }}>
                             {index + 1}위
                           </div>
                         )}
                         <div style={{
                           width: '90px',
                           display: 'flex',
                           flexDirection: 'row',
                           gap: '4px',
                           alignItems: 'center',
                           flexShrink: '0'
                         }}>
                           <div style={{
                             height: '20px',
                             width: '20px',
                             display: 'flex',
                           }}>
                             <img src="/images/icons/profileimage.svg" alt="프로필 아이콘" />
                           </div>
                           <div style={{
                             width: '60px',
                             display: 'flex',
                             flexShrink: '0',
                             color: 'var(--Color-text-normal, #161616)',
                             fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                             fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                             fontStyle: 'normal',
                             fontWeight: '400',
                             lineHeight: '150%',
                             cursor: 'pointer',
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}
                           onClick={() => handleStudentClick(student)}
                           title={student.name}
                           >
                             {student.name.length > 4 ? `${student.name.substring(0, 4)}...` : student.name}
              </div>
                         </div>
                         <div style={{
                           width: '29px',
                           display: 'flex',
                           alignItems: 'center',
                           color: 'var(--Color-text-normal, #161616)',
                           fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                           fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                           fontStyle: 'normal',
                           fontWeight: '400',
                           lineHeight: '150%',
                         }}>
                           {student.studentNumber || '-'}
            </div>
            <div style={{ 
                           width: '47px',
              display: 'flex', 
                           alignItems: 'center',
                           color: 'var(--Color-text-normal, #161616)',
                           fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                           fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                           fontStyle: 'normal',
                           fontWeight: '400',
                           lineHeight: '150%',
                         }}>
                           {student.points}
                         </div>
                     </div>
                   ))}
                </div>
                </div>
              </div>
            </Card>
            {/* 아이템 승인 요청 */}
            <Card data-block="approval-requests"
              width={isMobile ? "100%" : "518px"}
              height={isMobile ? "auto" : "100%"}
              padding="16px 20px"
              alignItems="flex-start"
              gap={16}
              flexDirection="column"
              style={{
                display: 'inline-flex',
                minHeight: isMobile ? "auto" : "100%",
                maxHeight: isMobile ? "none" : "100%",
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
                {/* 헤더 영역 - 고정 */}
                <div data-block="approval-requests-header" style={{
                  width: '100%',
                  height: '50px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  flexShrink: '0 0',
                  boxSizing: 'border-box',
                }}>
                  <div data-block="approval-requests-header-title" style={{
                  width: '100%',
                  height: '27px',
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, pretendard)',
                  fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '150%', /* 27px */
                  letterSpacing: '-0.18px',
                  justifyContent: 'center',
                  margin: 0,
                  boxSizing: 'border-box',
                  }}>
                  아이템 사용 승인 요청
                </div>
                  <div data-block="approval-requests-header-subtitle" style={{
                    width: '100%', 
                    height: '21px',
                    justifyContent: 'center', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    color: 'var(--Color-text-assistive, #989898)', 
                    fontSize: 14, 
                    fontFamily: 'pretendard',
                    fontWeight: '400', 
                    lineHeight: 21, 
                    wordWrap: 'break-word',
                    margin: 0,
                    boxSizing: 'border-box',
                    }}>
                      총 {approvalRequests.length}개
                    </div>
                </div>
                <div data-block="approval-requests-table-container" style={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                }}>
                {/* 표 제목 영역 - 고정 */}
                <div data-block="approval-requests-table-header" style={{
                  width: '100%',
                  height: '28px',
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '4px 8px',
                  gap: '24px',
                  borderRadius: 'var(--common-8, 8px)',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%', /* 19.5px */
                  flexShrink: 0,
                  boxSizing: 'border-box',
                }}> 
                  <div data-block="approval-requests-table-header-no" style={{
                    width: '17px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                  }}>
                    No
                    </div>
                  <div data-block="approval-requests-table-header-name" style={{
                    width: '104px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                      }}>
                        이름
                    </div>
                  <div data-block="approval-requests-table-header-item" style={{
                    width: '77px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                  }}>
                    아이템
                  </div>
                  <div data-block="approval-requests-table-header-date" style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexShrink: '0',
                    boxSizing: 'border-box',
                  }}>
                    요청일시
                  </div>
                </div>
                
                {/* 스크롤 영역 - 승인 요청 목록만 */}
                <div data-block="approval-requests-table-content" style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                  boxSizing: 'border-box',
                }}>
                {/* 포인트 표 내용 영역*/}
                {approvalRequests.length > 0 ? (
                  approvalRequests.map((request, index) => (
                    <div key={request.id} data-block="approval-requests-table-content-item" style={{
                      width: '100%',
                      minHeight: '41px',
                      height: '41px',
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '8px',
                      borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',      
                      overflow: 'hidden',
                      boxSizing: 'border-box'
                    }}>
                      <div data-block="approval-requests-table-content-item-row" style={{
                        width: '100%', 
                        minHeight: '25px',
                        height: '25px', 
                        gap: '24px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center', 
                        flex: 1
                      }}>
                        <div data-block="approval-requests-table-content-item-row-no" style={{
                          width: '17px', 
                          minHeight: '25px',
                          height: '25px',
                          boxSizing: 'border-box',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexShrink: '0',
                          color: 'var(--Color-text-normal, #161616)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          wordWrap: 'break-word',
                          whiteSpace: 'nowrap'}}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div data-block="approval-requests-table-content-item-row-name" style={{
                          width: '104px', 
                          minHeight: '25px',
                          height: '25px',
                          boxSizing: 'border-box',
                          justifyContent: 'center', 
                          alignItems: 'flex-start',
                          display: 'flex', 
                          flexShrink: '0',
                          flexDirection: 'column', 
                          color: 'var(--Color-text-brand-color, #FF6F0F)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          overflow: 'hidden',
                          cursor: 'pointer'
                        }}
                        title={request.studentName}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewStudentItems(request.studentId, request.studentName);
                        }}
                        onMouseEnter={(e) => {
                          const label = e.currentTarget.firstElementChild as HTMLElement | null;
                          if (label) label.style.fontWeight = '600';
                        }}
                        onMouseLeave={(e) => {
                          const label = e.currentTarget.firstElementChild as HTMLElement | null;
                          if (label) label.style.fontWeight = '400';
                        }}
                        >
                          <span style={{
                            display: 'block',
                            width: '100%',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            textDecoration: 'underline'
                          }}>
                            {request.studentName}
                          </span>
                        </div>
                        <div style={{
                          width: '77px',
                          minHeight: '25px',
                          height: '25px',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexShrink: '0',
                          color: 'var(--Color-text-normal, #161616)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          overflow: 'hidden'
                        }}
                        title={request.itemName}
                        >
                          <span style={{
                            display: 'block',
                            width: '100%',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                          }}>
                            {request.itemName}
                          </span>
                        </div>
                        <div style={{
                          justifyContent: 'center', 
                          alignItems: 'flex-start',
                          width: '103px',
                          minHeight: '25px',
                          height: '25px',
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexShrink: '0',
                          color: 'var(--Color-text-normal, #161616)', 
                          fontSize: '13px', 
                          fontFamily: 'Pretendard', 
                          fontWeight: '400', 
                          lineHeight: '19.50px', 
                          wordWrap: 'break-word',
                          whiteSpace: 'nowrap'}}>
                          {request.createdAt.toISOString().slice(0, 16).replace('T', ' ').replace(/-/g, '.')}
                        </div>
                        <div style={{
                          paddingLeft: '17px', 
                          paddingRight: '17px', 
                          paddingTop: '4px', 
                          paddingBottom: '4px', 
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          width: '54px', 
                          height: '25px',
                          flexShrink: '0',
                          background: 'var(--Color-fill-normal, #293044)', 
                          borderRadius: '4px',
                          boxSizing: 'border-box',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setSelectedRequest(request);
                          setOpenApprovalDialog(true);
                        }}>
                          <div style={{
                            color: 'white', 
                            fontSize: 11,
                            fontFamily: 'Pretendard', 
                            fontWeight: '400',
                            lineHeight: '16.50px',
                          }}>
                            승인
                </div>
              </div>
            </div>
          </div>
                  ))
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard'
                  }}>
                    승인 요청이 없습니다
        </div>
                )}
                  
                </div>
                </div>
          </Card>
          </div>
          )}
        </div>
        {currentUser?.schoolId && currentUser?.classId && (
          <AddClassPointsDialog
            isOpen={isAddClassPointsDialogOpen}
            onClose={() => setIsAddClassPointsDialogOpen(false)}
            schoolId={currentUser.schoolId}
            classId={currentUser.classId}
            currentClassPoints={classPoints}
            teacherId={currentUser.uid}
            teacherName={currentUser.name || '선생님'}
            onSuccess={() => {
              getClassPoints(currentUser.schoolId, currentUser.classId!).then(setClassPoints);
            }}
          />
        )}

        {/* 반, 달력, 포인트, 아이탬 승인 영역*/}
        
        {/* 시간표 - 모바일에서 숨김 */}
        {!isMobile && (
        <Card data-block="timetable"
        width={isMobile ? "100%" : "411px"} 
        height="auto"
        padding="16px 20px" 
        alignItems="flex-start" 
        gap={isMobile ? "16px" : "24px"} 
        style={{
          display: 'flex',
          boxSizing: 'border-box',
          flexShrink: '0',
          flexDirection: 'column',
          minHeight: 'auto'
        }}>
          {/* 시간표 제목 영역*/}
        <div data-block="timetable-header" style={{ 
            width: '100%',
            height: '50px',
            boxSizing: 'border-box',
            display: 'inline-flex',
            flexDirection: 'row',
            gap: '8px'
          }}>
            <div style={{
              width: '243px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              boxSizing: 'border-box',
            }}>
              <div data-block="timetable-header-title" style={{alignSelf: 'stretch', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'var(--Color-text-normal, #161616)', fontSize: '18px', fontFamily: 'Pretendard', fontWeight: '600', lineHeight: '27px', wordWrap: 'break-word'}}>시간표</div>
              <div data-block="timetable-header-subtitle" style={{alignSelf: 'stretch', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'var(--Color-text-assistive, #989898)', fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '400', lineHeight: '21px', wordWrap: 'break-word'}}>{timetableData.year}년 {timetableData.semester}</div>
            </div>
            <Button data-block="timetable-header-button" 
                variant='edit'
                width='120px'
                height='40px'
                size='small'
                onClick={() => setIsTimetableDialogOpen(true)}
                leftIcon={<EditIcon width={14} height={14} color="#4C4C4C" />}
              >시간표 수정하기</Button>
              
          </div>
          {/* 시간표 내용 영역*/}
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* 시간표 현재 교과 안내 영역*/}
            <div style={{
              width: '100%', 
              justifyContent: 'flex-start', 
              alignItems: 'center',
              boxSizing: 'border-box',
              display: 'flex', 
              flexDirection: 'row',
              gap: '4px',
              color: 'var(--Color-text-normal, #161616)', 
              fontSize: '18px', 
              fontFamily: 'Pretendard', 
              fontWeight: '600', 
              lineHeight: '27px', 
              wordWrap: 'break-word'}}>
                지금은 
                <span data-block="timetable-header-current-period" style={{
                  color: 'var(--Color-text-brand-color, #FF6F0F)'}}>
                  {currentTimetableStatus?.kind === 'period'
                    ? `${currentTimetableStatus.period}교시 ${currentTimetableStatus.subject}`
                    : currentTimetableStatus?.kind === 'lunch'
                      ? '점심시간'
                      : '수업 시간이 아닙니다'}
                          </span>
                {currentTimetableStatus?.kind === 'period' && ' 시간입니다'}
                {currentTimetableStatus?.kind === 'lunch' && '입니다'}
              </div>
            {/* 시간표 시간표 영역*/}
              <div data-block="timetable-content" style={{ 
              width: '100%',
              height: 'auto',
              boxSizing: 'border-box',
                display: 'flex', 
                flexDirection: 'column', 
              gap: '12px'
            }}>
              {/* 시간표 요일 영역*/}
              <div data-block="timetable-days" style={{
              width: '100%',
              height: '28px',
              display: 'flex',
              boxSizing: 'border-box',
              flexDirection: 'row',
              gap: '50px',
              fontSize: '13px',
              fontFamily: 'Pretendard',
              fontWeight: '500',
              lineHeight: '19.50px',
              wordWrap: 'break-word',
              padding: '4px 29px 4px 82px', 
              background: 'var(--Color-background-sub, #F8F8F8)', 
              borderRadius: '8px', 
              justifyContent: 'center', 
              alignItems: 'center'
              }}>
                <div style={{
                  width: '12px',
                  height: '100%',
                  color: new Date().getDay() === 1 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  월
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 2 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  화
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 3 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  수
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 4 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  목
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  color: new Date().getDay() === 5 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  금
                </div>
              </div>
              {/* 시간표 시간표 영역*/}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',  
                boxSizing: 'border-box',
              }}>
                {/* 동적으로 시간표 렌더링 */}
                {timetableData.entries.slice(0, 4).map((entry) => (
                  <div key={entry.period} style={{
                    height: '48px',
                    gap: '16px',
                    boxSizing: 'border-box',
                    padding: '0px 8px 10px 8px',
                    display: 'inline-flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center', 
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontFamily: 'Pretendard',
                    wordWrap: 'break-word',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    {/* 교시 및 시간*/}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                justifyContent: 'center',
                        fontSize: '13px',
                        lineHeight: '19.50px',
                        display: 'flex',
                        color: 'var(--Color-text-normal, #161616)' 
                      }}>
                        {entry.period}교시
              </div>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '12px',
                        lineHeight: '18px',
                        width: '33px',
                        flexShrink: '0',
                        display: 'flex',
                        color: 'var(--Color-text-assistive, #989898)' 
                      }}>
                        {entry.time}
            </div>
          </div>
                    {/* 과목 영역*/}
                    <div style={{
                      display: 'flex',
                      width: '305px',
                      height: '20px',
                      padding: '0px 4px',
                      gap: '13px',
                      fontSize: '13px',
                      flexDirection: 'row',
                      lineHeight: '19.50px',
                      wordWrap: 'break-word',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      flexShrink: '0'
                    }}>
                      <span style={{ width: '49px' }}>{entry.monday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.tuesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.wednesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.thursday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.friday || '-'}</span>
        </div>
                  </div>
                ))}
                
                {/* 점심시간 */}
                <div style={{
                  height: '48px',
                  gap: '18px',
                  boxSizing: 'border-box',
                  padding: '0px 8px 10px 8px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center', 
                  fontStyle: 'normal',
                  fontWeight: '500',
                  wordWrap: 'break-word',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  fontSize: '13px',
                  lineHeight: '19.50px',
                  color: 'var(--Color-text-normal, #161616)'
                }}>
                  😋 점심시간
                </div>

                {/* 5-7교시 */}
                {timetableData.entries.slice(4, 7).map((entry) => (
                  <div key={entry.period} style={{
                    height: '48px',
                    boxSizing: 'border-box',
                    gap: '18px',
                    padding: '0px 8px 10px 8px',
                    display: 'inline-flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontFamily: 'Pretendard',
                    wordWrap: 'break-word',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    {/* 교시 및 시간*/}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '13px',
                        lineHeight: '19.50px',
                        display: 'flex',
                        color: 'var(--Color-text-normal, #161616)' 
                      }}>
                        {entry.period}교시
                      </div>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '12px',
                        lineHeight: '18px',
                        width: '33px',
                        flexShrink: '0',
                        display: 'flex',
                        color: 'var(--Color-text-assistive, #989898)' 
                      }}>
                        {entry.time}
                      </div>
                    </div>
                    {/* 과목 영역*/}
                    <div style={{
                      display: 'flex',
                      width: '305px',
                      height: '20px',
                      padding: '0px 4px',
                      gap: '13px',
                      fontSize: '13px',
                      flexDirection: 'row',
                      lineHeight: '19.50px',
                      wordWrap: 'break-word',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      flexShrink: '0'
                    }}>
                      <span style={{ width: '49px' }}>{entry.monday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.tuesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.wednesday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.thursday || '-'}</span>
                      <span style={{ width: '49px' }}>{entry.friday || '-'}</span>
                    </div>
                  </div>
                ))}
            </div>
            
          </div>
          </div>
        </Card>
        )}
      </div>

      {/* 학급 포인트 + 오늘 급식 */}
      <div
        data-block="points-meal-row"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
          gap: 16,
          marginTop: 20,
          boxSizing: 'border-box',
        }}
      >
        {currentUser?.schoolId && currentUser?.classId && (
          <Card
            data-block="class-total-points"
            width="100%"
            padding="16px 20px"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'stretch',
              gap: 12,
              boxSizing: 'border-box',
              flex: isMobile ? undefined : '1 1 0',
              minWidth: 0,
            }}
          >
            <span style={{
              color: 'var(--Color-text-assistive, #989898)',
              fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '21px',
            }}>
              학급 전체 포인트
            </span>
            <span style={{
              color: 'var(--Color-text-strong, #0D0D0D)',
              fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
              fontSize: 36,
              fontWeight: 600,
              lineHeight: '48px',
              letterSpacing: '-0.02em',
            }}>
              {classPoints.toLocaleString()}
              <span style={{
                marginLeft: 4,
                fontSize: 18,
                fontWeight: 500,
                color: 'var(--Color-text-assistive, #989898)',
              }}>
                pt
              </span>
            </span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 'auto',
              width: '100%',
            }}>
              <Button variant="line" size="small" onClick={() => navigate('/class/teacher/ClassPointManagement')}>
                관리
              </Button>
              <Button variant="primary" size="small" onClick={() => setIsAddClassPointsDialogOpen(true)}>
                지급/차감
              </Button>
            </div>
          </Card>
        )}
        <TodayMealCard
          officeCode={schoolInfo?.neisOfficeCode}
          schoolCode={schoolInfo?.neisSchoolCode}
          width="100%"
          style={
            isMobile
              ? undefined
              : {
                  flex: '1 1 0',
                  minWidth: 0,
                  width: '100%',
                }
          }
        />
      </div>

      {/* 학생 상세 정보 다이얼로그 */}
      {openStudentDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '16px 20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px', fontSize: '20px' }}>👤</span>
                <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
              {selectedStudent?.name} 학생 정보
                </h3>
              </div>
              <button 
            onClick={() => setOpenStudentDialog(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 0, flex: 1, overflow: 'auto' }}>
              <div style={{ borderBottom: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', width: '100%' }}>
                  <button
                    onClick={() => setTabValue(0)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: 'none',
                      backgroundColor: tabValue === 0 ? '#1976d2' : 'transparent',
                      color: tabValue === 0 ? 'white' : '#666',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    포인트 내역
                  </button>
                </div>
              </div>
          
          {/* 포인트 내역 탭 */}
          {tabValue === 0 && (
                <div style={{ padding: '16px' }}>
                  <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{ margin: 0, fontSize: '1.125rem' }}>
                      현재 포인트: <span style={{ 
                        backgroundColor: '#1976d2', 
                        color: 'white', 
                        padding: '4px 8px', 
                        borderRadius: '12px', 
                        fontSize: '0.875rem',
                        fontWeight: 'bold'
                      }}>
                        {selectedStudent?.points}
                      </span>
                    </h4>
                  </div>
              
              {loadingHistory ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                      <div>로딩 중...</div>
                    </div>
              ) : pointHistory.length > 0 ? (
                    <div>
                  {pointHistory.map((history) => (
                        <div 
                      key={history.id}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 0',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ 
                            backgroundColor: history.amount > 0 ? '#4caf50' : '#f44336',
                            color: 'white',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '16px',
                            fontSize: '18px'
                          }}>
                            {history.amount > 0 ? '+' : '-'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '1rem',
                              fontWeight: '500',
                              marginBottom: '4px'
                            }}>
                            {history.amount > 0 ? '포인트 지급' : '포인트 차감'} - {history.amount}점
                            </div>
                            <div style={{ 
                              fontSize: '0.875rem',
                              color: '#666',
                              marginBottom: '2px'
                            }}>
                              {history.reason}
                            </div>
                            <div style={{ 
                              fontSize: '0.75rem',
                              color: '#999'
                            }}>
                              {history.createdAt.toLocaleString()} | {history.teacherName} 교사
                            </div>
                          </div>
                          <div>
                            <span style={{ 
                              backgroundColor: history.amount > 0 ? '#4caf50' : '#f44336',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              {history.amount > 0 ? `+${history.amount}` : `${history.amount}`}
                          </span>
                          </div>
                        </div>
                      ))}
                    </div>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: '32px'
                }}>
                  <div style={{ fontSize: '60px', color: '#666', marginBottom: '16px' }}>📊</div>
                  <p style={{ color: '#666', margin: 0, fontSize: '1rem' }}>
                    포인트 내역이 없습니다.
                  </p>
                </div>
              )}
                </div>
              )}
            </div>
            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid #e0e0e0',
          display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                onClick={() => setOpenStudentDialog(false)}
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 승인 요청 다이얼로그 */}
      {openApprovalDialog && (
        <div data-block="approval-requests-dialog" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
          zIndex: 1000
        }}>
          <div data-block="approval-requests-dialog-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div data-block="approval-requests-dialog-header" style={{ 
              padding: '20px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h3 data-block="approval-requests-dialog-header-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>
          아이템 사용 승인 요청
              </h3>
            </div>
            <div data-block="approval-requests-dialog-content" style={{ padding: '20px', flex: 1, overflow: 'auto' }}>
          {selectedRequest && (
                <div>
                  <div data-block="approval-requests-dialog-content-item" style={{ marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '500' }}>
                학생: {selectedRequest.studentName}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '500' }}>
                아이템: {selectedRequest.itemName}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', color: '#666' }}>
                아이템 타입: {selectedRequest.targetType === 'class' ? '학급 아이템' : '교과 아이템'}
                    </p>
                    <p style={{ margin: '0 0 16px 0', fontSize: '0.875rem', color: '#666' }}>
                요청일시: {selectedRequest.createdAt.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '0.875rem', 
                      fontWeight: '500' 
                    }}>
                      거절 사유 (선택)
                    </label>
                    <textarea
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                      placeholder="거절 사유를 입력하세요..."
                onChange={(e) => setSelectedRequest({
                  ...selectedRequest,
                  reason: e.target.value
                })}
              />
                  </div>
                </div>
              )}
            </div>
            <div style={{ 
              padding: '20px', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <button 
                onClick={() => setOpenApprovalDialog(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                취소
              </button>
              <button 
            onClick={() => selectedRequest && handleApprovalRequest(selectedRequest, false, selectedRequest.reason)}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                거절
              </button>
              <button 
            onClick={() => {
              if (selectedRequest) {
                handleApprovalRequest(selectedRequest, true);
              }
            }}
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                승인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 학생 아이템 조회 다이얼로그 */}
      {openStudentItemsDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Card data-block="student-items-dialog" width="600px" height="500px" padding="24px 28px">
            {/* 헤더 */}
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#161616',
                margin: 0
              }}>
                {selectedStudentForItems?.name} 학생의 아이템
              </h2>
              <img 
                src="/images/icons/closeIcon.svg"
                alt="닫기"
                onClick={() => {
                  setOpenStudentItemsDialog(false);
                  setSelectedStudentForItems(null);
                  setStudentItems([]);
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  padding: '9.4px',
                  cursor: 'pointer'
                }}
              />
            </div>

            {/* 아이템 목록 */}
            <div style={{
              width: '100%',
              height: '400px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {studentItems.length > 0 ? (
                studentItems.map((item) => (
                  <div key={item.id} style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid var(--Color-line-normal, #F0F0F0)',
                    borderRadius: '8px',
                    background: 'var(--Color-background-normal, #FFF)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#161616',
                        margin: 0
                      }}>
                        {item.name}
                      </h3>
                      <div style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: item.status === 'available' 
                          ? '#E8F5E9' 
                          : '#FFF3E0',
                        color: item.status === 'available' 
                          ? '#2E7D32' 
                          : '#EF6C00'
                      }}>
                        {item.status === 'available' ? '사용 가능' : '승인 대기'}
                      </div>
                    </div>
                    {item.description && (
                      <p style={{
                        fontSize: '13px',
                        color: '#4C4C4C',
                        margin: 0
                      }}>
                        {item.description}
                      </p>
                    )}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#989898'
                    }}>
                      <span>타입: {item.type === 'class' ? '학급 아이템' : '교과 아이템'}</span>
                      {item.purchasedAt && (
                        <span>구매일: {item.purchasedAt.toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#989898'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                  <p style={{ fontSize: '14px', margin: 0 }}>보유한 아이템이 없습니다.</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* 시간표 수정 다이얼로그 */}
      <TimetableEditDialog
        isOpen={isTimetableDialogOpen}
        initialData={timetableData}
        neisContext={
          schoolInfo?.neisOfficeCode && schoolInfo?.neisSchoolCode && currentUser?.homeroomClass
            ? {
                neisOfficeCode: schoolInfo.neisOfficeCode,
                neisSchoolCode: schoolInfo.neisSchoolCode,
                schoolType: schoolInfo.type,
                grade: currentUser.homeroomClass.grade,
                classNum: currentUser.homeroomClass.class,
              }
            : null
        }
        comciganContext={
          schoolInfo?.comciganSchoolCode && currentUser?.homeroomClass
            ? {
                schoolCode: schoolInfo.comciganSchoolCode,
                grade: currentUser.homeroomClass.grade,
                classNum: currentUser.homeroomClass.class,
              }
            : null
        }
        onClose={() => setIsTimetableDialogOpen(false)}
        onTimetableSaved={async (timetable) => {
          try {
            if (!currentUser?.schoolId || !currentUser?.classId) {
              setError('학교 또는 학급 정보가 없습니다.');
              return;
            }

            // Firestore에 시간표 저장
            const timetableRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/timetable/current`);
            await setDoc(timetableRef, {
              semester: timetable.semester,
              year: timetable.year,
              entries: timetable.entries,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser.uid,
              updatedByName: currentUser.name
            });

            // 로컬 상태 업데이트
            setTimetableData(timetable);
            setIsTimetableDialogOpen(false);
            setSuccess('시간표가 성공적으로 저장되었습니다.');
          } catch (err) {
            console.error('시간표 저장 중 오류:', err);
            setError('시간표 저장 중 오류가 발생했습니다.');
          }
        }}
      />

      {/* 알림 다이얼로그 */}
      {openNotificationDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '16px 20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px', fontSize: '20px' }}>🔔</span>
                <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                  알림 ({notifications.length}개)
                </h3>
              </div>
              <button 
                onClick={handleNotificationClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '16px', flex: 1, overflow: 'auto' }}>
              {notifications.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id}
                      style={{ 
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: getNotificationBackgroundColor(notification),
                        cursor: notification.actionUrl ? 'pointer' : 'default',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (notification.actionUrl) {
                          e.currentTarget.style.transform = 'translateX(4px)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (notification.actionUrl) {
                          e.currentTarget.style.transform = 'translateX(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                      onClick={() => {
                        if (!notification.isRead) {
                          handleMarkNotificationAsRead(notification.id);
                        }
                        // actionUrl이 있으면 해당 페이지로 이동
                        if (notification.actionUrl) {
                          navigate(notification.actionUrl);
                          handleNotificationClose();
                        }
                      }}
                    >
                      <div style={{ 
                        backgroundColor: getNotificationTypeColor(notification.type),
                        color: 'white',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px',
                        fontSize: '16px',
                        flexShrink: 0
                      }}>
                        {getNotificationTypeIcon(notification.type)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontSize: '1rem',
                          fontWeight: notification.isRead ? '400' : '600',
                          marginBottom: '4px',
                          color: notification.isRead ? '#666' : '#000'
                        }}>
                          {notification.title}
                        </div>
                        <div style={{ 
                          fontSize: '0.875rem',
                          color: '#666',
                          marginBottom: '4px'
                        }}>
                          {notification.message}
                        </div>
                        <div style={{ 
                          fontSize: '0.75rem',
                          color: '#999',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {notification.createdAt.toLocaleString()}
                          {notification.senderName && (
                            <span style={{ fontSize: '0.7rem' }}>
                              • {notification.senderName}
                            </span>
                          )}
                          {notification.actionUrl && (
                            <span style={{ 
                              fontSize: '0.7rem', 
                              color: '#1976d2',
                              textDecoration: 'underline'
                            }}>
                              이동하기 →
                            </span>
                          )}
                        </div>
                      </div>
                      {!notification.isRead && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#1976d2',
                          borderRadius: '50%',
                          flexShrink: 0,
                          marginTop: '4px'
                        }} />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    알림이 없습니다.
                  </p>
                </div>
              )}
            </div>
            
            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <button 
                onClick={handleMarkAllNotificationsAsRead}
                disabled={notifications.filter(n => !n.isRead).length === 0}
                style={{
                  backgroundColor: notifications.filter(n => !n.isRead).length === 0 ? '#ccc' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: notifications.filter(n => !n.isRead).length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                모두 읽음 처리
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    handleNotificationClose();
                    navigate('/class/teacher/MessageInbox');
                  }}
                  style={{
                    backgroundColor: '#FF6F0F',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  메시지함
                </button>
                <button 
                  onClick={handleNotificationClose}
                  style={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 아이템이 없는 승인 요청 삭제 확인 다이얼로그 */}
      {openItemNotFoundDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)'
          }}>
            <div style={{
              fontSize: '18px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              color: '#161616',
              marginBottom: '8px'
            }}>
              ⚠️ 아이템이 존재하지 않습니다
            </div>
            <div style={{
              fontSize: '14px',
              fontFamily: 'Pretendard',
              fontWeight: '400',
              color: '#4C4C4C',
              marginBottom: '20px',
              lineHeight: '20px'
            }}>
              <strong>{itemNotFoundRequest?.studentName}</strong> 학생의<br/>
              <strong>"{itemNotFoundRequest?.itemName}"</strong> 아이템이<br/>
              더 이상 존재하지 않습니다.<br/><br/>
              승인 요청을 삭제하시겠습니까?
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  setOpenItemNotFoundDialog(false);
                  setItemNotFoundRequest(null);
                  setOpenApprovalDialog(false); // 아이템 사용 다이얼로그도 닫기
                  setSelectedRequest(null);
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#4C4C4C',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500'
                }}
              >
                취소
              </button>
              <button
                onClick={handleDeleteItemNotFoundRequest}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#FF6F0F',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500'
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      <AddNewNoticeDialog
        isOpen={isAddNoticeDialogOpen}
        onClose={() => setIsAddNoticeDialogOpen(false)}
        notices={noticeItems}
        onAddNotice={handleAddNotice}
        onDeleteNotices={handleDeleteNotices}
      />
    </div>
  );
};

export default ClassTeacherHome; 
